"use client";
import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import DeveloperService from "@/services/developer";
import PropertyListingCard from "@/components/bnp-ui/listing-card/PropertyListingCard";
import PropertyCtaBanner from "@/components/shared/PropertyCtaBanner";
import { LoadMore, Breadcrumb } from "@/components/shared";
import {
  IconPhone,
  IconMail,
  IconMapPin,
  IconCalendar,
  IconBuilding,
  IconArrowRight,
  IconLayoutGrid,
  IconHome,
  IconKey,
  IconMap,
  IconFileText,
  IconClock,
  IconShare,
  IconMessageCircle,
  IconX,
  IconCircleCheck,
} from "@tabler/icons-react";
import { Property } from "@/types/property";
import { buildPropertyDetailsUrl } from "@/utils/propertyReferrer";

// --- Types ---
interface ApiDeveloper {
  id: string;
  full_name: string;
  email: string;
  phone_number: string;
  business_name?: string;
  business_address?: string;
  address?: string;
  city?: string;
  state?: string;
  listed_properties_count?: number;
  join_date: string;
  profile_image_url?: string | null;
  kyc_status?: string;
  slug?: string;
  bio?: string;
  plan?: string;
}

interface Developer {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  business_name?: string | null;
  business_address?: string | null;
  city?: string;
  state?: string;
  bio?: string;
  total_properties?: number;
  verified?: boolean;
  join_date: string;
  photo?: string | null;
  plan?: string | null;
  slug?: string | null;
}

interface StatCount {
  total: number;
  sale: number;
  rent: number;
  shortlet: number;
  lease: number;
  land: number;
}

interface DeveloperProfilePageProps {
  initialDeveloper: Developer | null;
  initialProperties: Property[];
  initialTotalCount: number;
  initialStatCount: StatCount | null;
}

const SITE_ORIGIN =
  process.env.NEXT_PUBLIC_BASE_URL || "https://www.findnigeriaproperties.com";

const getAbsoluteUrl = (value?: string) => {
  if (!value) return `${SITE_ORIGIN}/logo.png`;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("//")) return `https:${value}`;
  if (value.startsWith("/")) return `${SITE_ORIGIN}${value}`;
  return `${SITE_ORIGIN}/${value}`;
};

const nullableString = (value?: string | null) => value ?? null;

const mapApiDeveloperToUi = (data: ApiDeveloper): Developer => ({
  id: data.id,
  full_name: data.full_name || "",
  email: data.email || "",
  phone: data.phone_number || "",
  business_name: data.business_name || data.full_name || null,
  business_address: nullableString(data.business_address),
  city: data.city || "",
  state: data.state || "",
  bio: data.bio || data.business_address || "",
  total_properties: data.listed_properties_count ?? 0,
  verified: data.kyc_status === "verified",
  join_date: data.join_date,
  photo: nullableString(data.profile_image_url),
  plan: nullableString(data.plan),
  slug: nullableString(data.slug),
});
// Mirrors the realtor mapper: spreading `...property` keeps the real API
// values (is_negotiable, created_at, is_saved, impression/save counts,
// serviced, createdBy, ...) and we only coerce the numeric-looking fields to
// strings plus null-guard caution_fee (String(null) used to render "null").
const mapApiPropertyToUi = (
  property: Property,
  developerData: Developer,
): Property =>
  ({
    ...property,
    reference: property.id,
    currency: "NGN",
    price: String(property.price),
    agency_fees: String(property.agency_fees),
    legal_fees: String(property.legal_fees),
    caution_fee:
      property.caution_fee != null ? String(property.caution_fee) : null,
    total_package: String(property.total_package),
  }) as unknown as Property;

// --- Helpers ---
const formatJoinDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    year: "numeric",
  });
};

const formatPhoneDisplay = (phone: string) => {
  const cleaned = phone.replace(/\s+/g, "");
  if (cleaned.length === 11)
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  return phone;
};

const formatWhatsAppLink = (phone: string) => {
  const cleaned = phone.replace(/\s+/g, "");
  const formatted = cleaned.startsWith("0")
    ? "234" + cleaned.slice(1)
    : cleaned;
  return `https://wa.me/${formatted}`;
};

// --- WhatsApp Icon ---
const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// --- Category Config ---
const CATEGORIES = [
  { key: "all", label: "All Properties", Icon: IconLayoutGrid },
  { key: "sale", label: "For Sale", Icon: IconHome },
  { key: "rent", label: "For Rent", Icon: IconKey },
  { key: "land", label: "Land", Icon: IconMap },
  { key: "lease", label: "Lease", Icon: IconFileText },
  { key: "shortlet", label: "Shortlet", Icon: IconClock },
];

// --- Main Component ---
const DeveloperProfilePage: React.FC<DeveloperProfilePageProps> = ({
  initialDeveloper,
  initialProperties,
  initialTotalCount,
  initialStatCount,
}) => {
  const router = useRouter();
  const { developerid } = router.query;
  const [activeFilter, setActiveFilter] = useState("all");
  const [showContactModal, setShowContactModal] = useState(false);
  const [properties, setProperties] = useState<Property[]>(
    initialProperties || [],
  );
  const [loading, setLoading] = useState(false);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);
  const [developer, setDeveloper] = useState<Developer | null>(
    initialDeveloper,
  );
  const [developerLoading, setDeveloperLoading] = useState(false);
  const [developerError, setDeveloperError] = useState<string | null>(
    initialDeveloper ? null : "Developer not found.",
  );
  const developerData = developer;
  const developerRef = useRef<Developer | null>(initialDeveloper);
  const propertiesGridRef = useRef<HTMLDivElement | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(initialTotalCount || 0);
  const [statCount, setStatCount] = useState<StatCount>(
    initialStatCount || {
      total: 0,
      sale: 0,
      rent: 0,
      shortlet: 0,
      lease: 0,
      land: 0,
    },
  );

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const pageSize = 12;

  const fetchDeveloper = useCallback(async () => {
    if (!developerid || typeof developerid !== "string") return;
    setDeveloperLoading(true);
    setDeveloperError(null);
    try {
      const res = await DeveloperService.getDeveloperById(developerid);
      const data = res?.data?.data || res?.data || null;
      if (data) {
        const mapped = mapApiDeveloperToUi(data);
        developerRef.current = mapped;
        setDeveloper(mapped);
      } else {
        developerRef.current = null;
        setDeveloper(null);
        setDeveloperError("Developer not found.");
      }
    } catch (err: any) {
      console.error("Failed to fetch developer:", err);
      developerRef.current = null;
      setDeveloper(null);
      setDeveloperError("Unable to load this developer profile at this time.");
    } finally {
      setDeveloperLoading(false);
    }
  }, [developerid]);

  const fetchProperties = useCallback(
    async (page: number = 1, developerId?: string) => {
      const effectiveDeveloperId =
        developerId ||
        developerRef.current?.id ||
        (typeof developerid === "string" ? developerid : undefined);
      if (!effectiveDeveloperId) return;
      setLoading(true);
      setPropertiesError(null);
      try {
        const propertiesRes = await DeveloperService.getPropertiesByDeveloper(
          effectiveDeveloperId,
          {
            pageNumber: page,
            pageSize: pageSize,
          },
        );
        const propertiesData = propertiesRes?.data?.data || [];
        const parsed = Array.isArray(propertiesData) ? propertiesData : [];
        const mapped = parsed.map((p: Property) =>
          mapApiPropertyToUi(p, developerRef.current as Developer),
        );
        const total = propertiesRes?.data?.metadata?.totalCount || 0;
        setTotalCount(total);
        if (propertiesRes?.data?.statCount) {
          setStatCount(propertiesRes.data.statCount);
        }
        if (page === 1) {
          setProperties(mapped);
        } else {
          setProperties((prev) => [...prev, ...mapped]);
        }
      } catch (err: any) {
        console.error("Failed to fetch properties:", err);
        if (page === 1) {
          setProperties([]);
        }
        setPropertiesError("Unable to load properties at this time.");
      } finally {
        setLoading(false);
        setIsLoadingMore(false);
      }
    },
    [developerid, pageSize],
  );

  const handleLoadMore = async () => {
    if (isLoadingMore) return;
    setIsLoadingMore(true);
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    await fetchProperties(nextPage);
  };
  useEffect(() => {
    if (initialDeveloper) {
      developerRef.current = initialDeveloper;
    }
  }, [initialDeveloper]);

  const loadedRef = useRef(false);

  useEffect(() => {
    if (loadedRef.current) return;
    if (!developerid || typeof developerid !== "string") return;
    loadedRef.current = true;
    let cancelled = false;

    const load = async () => {
      await fetchDeveloper();
      if (!cancelled) {
        await fetchProperties(1, developerRef.current?.id || undefined);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [developerid, fetchDeveloper, fetchProperties]);

  const availableCategories = useMemo(() => {
    const cats = new Set(properties.map((p) => p.listing_type));
    return CATEGORIES.filter((c) => c.key === "all" || cats.has(c.key));
  }, [properties]);

  const filteredProperties = useMemo(() => {
    if (activeFilter === "all") return properties;
    return properties.filter((p) => p.listing_type === activeFilter);
  }, [activeFilter, properties]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {
      all: statCount.total || totalCount,
      sale: statCount.sale || 0,
      rent: statCount.rent || 0,
      shortlet: statCount.shortlet || 0,
      lease: statCount.lease || 0,
      land: statCount.land || 0,
    };
    return counts;
  }, [statCount, totalCount]);

  const developerName = developerData
    ? developerData.business_name || developerData.full_name
    : "";

  const handleShare = async () => {
    if (!developerData) return;

    const businessName =
      developerData.business_name || developerData.full_name || "Developer";
    const location =
      developerData.state ||
      developerData.city ||
      developerData.business_address ||
      "";
    const propertyCount = totalCount || developerData.total_properties || 0;
    const shareUrl = `${SITE_ORIGIN}/developers/${developerData.slug || developerData.id}`;

    const shareText =
      `Check out ${businessName} on Find Nigeria Properties\n\n` +
      `${propertyCount} propert${propertyCount === 1 ? "y" : "ies"} listed\n` +
      (location ? `Located in ${location}\n\n` : "\n") +
      `View my profile: ${shareUrl}`;

    const sharePayload = {
      title: businessName,
      text: shareText,
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        return;
      }
    } catch (error) {
      console.error("Native share failed:", error);
    }

    const fallbackUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(
      fallbackUrl,
      "share-developer",
      "width=600,height=500,scrollbars=yes,resizable=yes",
    );
  };

  // Skeleton only while the profile itself is genuinely loading on the client.
  // When SSR already supplied `initialDeveloper` this never shows — the endless
  // spinner happened because the route param was never injected into the compat
  // router query (no CompatParamsProvider on this route), so `developerid` was
  // permanently undefined and the fetches bailed out before clearing the flag.
  if (developerLoading && !developerData) {
    return (
      <HomeWrapper showHero={false}>
        <div className={containerWidth}>
          <div className="min-h-screen flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D96A7]" />
          </div>
        </div>
      </HomeWrapper>
    );
  }

  // Developer profile error state
  if (developerError || !developerData) {
    return (
      <HomeWrapper showHero={false}>
        <div className={containerWidth}>
          <div className="min-h-screen bg-white mt-40 flex items-center justify-center px-4">
            <div className="text-center max-w-md">
              <IconBuilding className="w-14 h-14 text-gray-300 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-800 mb-2">
                Developer not found
              </h1>
              <p className="text-sm text-gray-500 mb-6">
                {developerError || "This developer profile could not be found."}
              </p>
              <button
                onClick={() => router.push("/developers")}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#2D96A7] hover:bg-[#2D96A7]/90 text-white text-sm font-semibold rounded-xl transition-all duration-300"
              >
                Browse Developers
                <IconArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </HomeWrapper>
    );
  }

  const displayName = developerData.full_name || developerName || "Developer";
  const displayLocation =
    developerData.state ||
    developerData.city ||
    developerData.business_address ||
    "";
  const displayPhone = developerData.phone || "";
  const displayEmail = developerData.email || "";
  return (
    <HomeWrapper showHero={false}>
      <div className={containerWidth}>
        <div className="min-h-screen bg-white mt-40">
          {/* ===== BREADCRUMB ===== */}
          <Breadcrumb
            items={[
              { label: "Home", href: "/" },
              { label: "Developers", href: "/developers" },
              { label: `${displayName}` },
            ]}
          />

          {/* ===== DEVELOPER PROFILE BANNER ===== */}
          <section className="relative overflow-hidden bg-gradient-to-br from-[#2D96A7]/5 via-white to-white">
            <div
              className="absolute inset-0 opacity-[0.03]"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%232D96A7' fillOpacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                {/* Cover */}
                <div className="relative h-36 md:h-48 bg-gradient-to-r from-[#2D96A7]/20 via-[#2D96A7]/5 to-white" />

                <div className="relative px-6 md:px-10 pb-8 -mt-16 md:-mt-20">
                  <div className="flex flex-col md:flex-row md:items-end gap-6">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl border-4 border-white overflow-hidden shadow-lg bg-gray-100">
                        {developerData.photo ? (
                          <img
                            src={developerData.photo}
                            alt={`${displayName}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-3xl font-bold text-gray-400">
                              {(displayName || "?")
                                .split(" ")
                                .map((n) => n[0])
                                .filter(Boolean)
                                .join("")
                                .slice(0, 2)
                                .toUpperCase()}
                            </span>
                          </div>
                        )}
                      </div>
                      {developerData.verified && (
                        <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#2D96A7] rounded-lg flex items-center justify-center shadow-lg shadow-[#2D96A7]/30">
                          <IconCircleCheck className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
{/* Info */}
                    <div className="flex-1 min-w-0 pb-1">
                      <div className="flex flex-wrap items-center gap-3 mb-1">
                        <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 tracking-tight">
                          {displayName}
                        </h1>
                        {developerData.verified && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#2D96A7]/10 text-[#2D96A7] border border-[#2D96A7]/30">
                            <IconCircleCheck className="w-3 h-3" />
                            Verified
                          </span>
                        )}
                      </div>
                      <p className="text-gray-500 text-sm md:text-base mb-3">
                        {developerData.business_name || "Property Developer"}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-400">
                        {displayLocation && (
                          <div className="flex items-center gap-1.5">
                            <IconMapPin className="w-4 h-4 text-[#2D96A7]" />
                            {displayLocation}
                          </div>
                        )}
                        {developerData.join_date && (
                          <div className="flex items-center gap-1.5">
                            <IconCalendar className="w-4 h-4 text-[#2D96A7]" />
                            Joined {formatJoinDate(developerData.join_date)}
                          </div>
                        )}
                        {developerData.plan && (
                          <span className="text-xs uppercase tracking-wider text-gray-400 bg-gray-50 px-2 py-0.5 rounded font-medium">
                            {developerData.plan}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 pb-1 flex-shrink-0">
                      <button
                        onClick={() => setShowContactModal(true)}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#2D96A7] hover:bg-[#2D96A7]/90 text-white text-sm font-semibold rounded-xl transition-all duration-300 shadow-sm hover:shadow-md"
                      >
                        <IconMessageCircle className="w-4 h-4" />
                        Contact
                      </button>
                      <button
                        onClick={handleShare}
                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold rounded-xl border border-gray-200 hover:border-gray-300 transition-all duration-300"
                      >
                        <IconShare className="w-4 h-4" />
                        Share
                      </button>
                    </div>
                  </div>

                  {/* Bio */}
                  {developerData.bio && (
                    <p className="mt-6 text-gray-500 text-sm leading-relaxed max-w-3xl">
                      {developerData.bio}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </section>
          {/* ===== PROPERTIES SECTION ===== */}
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 tracking-tight">
                  Properties by {developerName}
                </h2>
                <p className="text-gray-400 text-sm mt-1">
                  Showing {properties.length} of {totalCount} properties
                </p>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="mb-8">
              <div className="flex flex-wrap gap-2">
                {availableCategories.map((cat) => {
                  const isActive = activeFilter === cat.key;
                  const count = categoryCounts[cat.key] || 0;

                  return (
                    <button
                      key={cat.key}
                      onClick={() => setActiveFilter(cat.key)}
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 ${
                        isActive
                          ? "bg-[#2D96A7] text-white shadow-sm"
                          : "bg-white text-gray-500 border border-gray-200 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <cat.Icon className="w-4 h-4" />
                      {cat.label}
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-md ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-400"}`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Property Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D96A7]" />
              </div>
            ) : propertiesError ? (
              <div className="text-center py-20 bg-white border border-gray-200 rounded-2xl">
                <IconBuilding className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-500 mb-1">
                  Unable to load properties
                </h3>
                <p className="text-sm text-gray-400 mb-4">{propertiesError}</p>
                <button
                  onClick={() => fetchProperties(1)}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D96A7] hover:text-[#2D96A7]/80 transition-colors"
                >
                  Try again
                  <IconArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : filteredProperties.length > 0 ? (
              <div
                ref={propertiesGridRef}
                className="p-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 justify-items-center gap-5 w-full"
              >
                {filteredProperties.map((property) => (
                  <PropertyListingCard
                    key={property.id}
                    property={property}
                    onCardClick={(p) =>
                      router.push(
                        buildPropertyDetailsUrl(
                          p.id,
                          {
                            key: "developer",
                            id: developerData?.slug || developerData?.id,
                          },
                          p.realtor?.slug || p.realtor?.id,
                        ),
                      )
                    }
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white border border-gray-200 rounded-2xl">
                <IconBuilding className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-500 mb-1">
                  No properties found
                </h3>
                <p className="text-sm text-gray-400 mb-4">
                  This developer doesn&rsquo;t have any {activeFilter}{" "}
                  properties yet.
                </p>
                <button
                  onClick={() => setActiveFilter("all")}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#2D96A7] hover:text-[#2D96A7]/80 transition-colors"
                >
                  View all properties
                  <IconArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </section>

          <LoadMore
            hasMore={properties.length < totalCount}
            isLoading={loading || isLoadingMore}
            onLoadMore={handleLoadMore}
            loadedCount={properties.length}
            totalCount={totalCount}
            pageSize={pageSize}
            contentRef={propertiesGridRef as React.RefObject<HTMLElement>}
            className="w-full"
          />

          {/* ===== CTA BANNER ===== */}
          <PropertyCtaBanner />
        </div>

        {/* ===== CONTACT MODAL ===== */}
        {showContactModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowContactModal(false)}
            />
            <div className="relative w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
              <div className="relative px-6 pt-6 pb-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Contact {developerData.business_name || displayName}
                  </h3>
                  <button
                    onClick={() => setShowContactModal(false)}
                    className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-all duration-200"
                  >
                    <IconX className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="px-6 py-6 space-y-3">
                {displayPhone ? (
                  <a
                    href={`tel:${displayPhone}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-[#2D96A7]/5 border border-gray-100 hover:border-[#2D96A7]/20 transition-all duration-200 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-[#2D96A7]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#2D96A7]/20 transition-colors">
                      <IconPhone className="w-5 h-5 text-[#2D96A7]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                        Phone
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {formatPhoneDisplay(displayPhone)}
                      </div>
                    </div>
                  </a>
                ) : null}

                {displayPhone ? (
                  <a
                    href={formatWhatsAppLink(displayPhone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-green-50 border border-gray-100 hover:border-green-200 transition-all duration-200 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0 group-hover:bg-green-200 transition-colors">
                      <WhatsAppIcon className="w-5 h-5 text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                        WhatsApp
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {formatPhoneDisplay(displayPhone)}
                      </div>
                    </div>
                  </a>
                ) : null}

                {displayEmail ? (
                  <a
                    href={`mailto:${displayEmail}`}
                    className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-blue-50 border border-gray-100 hover:border-blue-200 transition-all duration-200 group"
                  >
                    <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-200 transition-colors">
                      <IconMail className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs text-gray-400 uppercase tracking-wider mb-0.5">
                        Email
                      </div>
                      <div className="text-sm font-medium text-gray-800 truncate">
                        {displayEmail}
                      </div>
                    </div>
                  </a>
                ) : null}
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <p className="text-xs text-gray-400 text-center">
                  By contacting this developer, you agree to our Terms of
                  Service
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </HomeWrapper>
  );
};

export default DeveloperProfilePage;
